import React, { useState } from "react";
import { AlertTriangle, Pencil, Plus, Trash2 } from "lucide-react";
import { RepositoryGroup } from "../../api/v2/types";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { buildGroupKeyFromName } from "../shared/utils/keyUtils";

interface GroupsManagerProps {
  groups: RepositoryGroup[];
  isBusy?: boolean;
  onCreateGroup: (payload: {
    groupKey: string;
    groupName: string;
    description?: string;
  }) => Promise<void>;
  onRenameGroup: (
    groupKey: string,
    payload: { groupName: string; description?: string }
  ) => Promise<void>;
  onDeleteGroup: (groupKey: string) => Promise<void>;
}

const GroupsManager = ({
  groups,
  isBusy,
  onCreateGroup,
  onRenameGroup,
  onDeleteGroup,
}: GroupsManagerProps) => {
  const [open, setOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [description, setDescription] = useState("");
  const [editing, setEditing] = useState<RepositoryGroup | null>(null);
  const groupKey = buildGroupKeyFromName(groupName);

  return (
    <div className="rounded border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Repository groups</h4>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-1" /> Add group
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create group</DialogTitle>
              <DialogDescription>Create a reusable group for bindings.</DialogDescription>
            </DialogHeader>
            <Input placeholder="Group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
            <Input readOnly value={groupKey} placeholder="Generated group key" className="bg-muted" />
            <Input
              placeholder="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <DialogFooter>
              <Button
                disabled={isBusy || !groupKey || !groupName.trim()}
                onClick={async () => {
                  await onCreateGroup({
                    groupKey,
                    groupName: groupName.trim(),
                    description: description.trim() || undefined,
                  });
                  setOpen(false);
                  setGroupName("");
                  setDescription("");
                }}
              >
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-1">
        {groups.map((group) => (
          <div key={group.groupKey} className="flex items-center justify-between border rounded px-2 py-1">
            <div className="min-w-0">
              <p className="text-sm truncate">{group.groupName}</p>
              <p className="text-xs text-muted-foreground truncate">{group.groupKey}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(group)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={group.isDefault}
                onClick={() => onDeleteGroup(group.groupKey)}
                title={group.isDefault ? "Default group cannot be deleted" : "Delete group"}
              >
                {group.isDefault ? <AlertTriangle className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(next) => !next && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename group</DialogTitle>
          </DialogHeader>
          <Input
            value={editing?.groupName || ""}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, groupName: e.target.value } : prev))
            }
          />
          <Input
            value={editing?.description || ""}
            onChange={(e) =>
              setEditing((prev) => (prev ? { ...prev, description: e.target.value } : prev))
            }
          />
          <DialogFooter>
            <Button
              disabled={!editing?.groupName || isBusy}
              onClick={async () => {
                if (!editing) return;
                await onRenameGroup(editing.groupKey, {
                  groupName: editing.groupName,
                  description: editing.description,
                });
                setEditing(null);
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsManager;
